import { computed, ComputedRef, Ref, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore';
import { SelectListItemStateType } from '@app/enums/SelectListItemStateType';
import type ISelectItem from '@app/interfaces/dtos/ISelectItem';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import type { IExtendedFilterValue } from '@app/interfaces/dtos/IExtendedFilterValue';
import type ISearchQueryFilter from '@domain/interfaces/dtos/ISearchQueryFilter';
import type { IOrganisationIdAndName } from '@studyportals/search-api-interface';
import type { ISearchResult } from '@domain/interfaces/dtos/ISearchResult';
import { environmentAwareFetch } from '@utils/environment-aware-fetch';
import debounce from '@utils/debounce';
import type { ModalStateFooter } from '@app/interfaces/modalState.interface';
import { useMobileModal } from './useMobileModal';
import { IFacetKeyValuePair } from '@domain/interfaces/dtos/IFacetKeyValuePair';
import { useRollbar } from '@composables/useRollbar';

type OrganisationFilter = {
	placeholder: string;
    multiselectValue: Ref<(string | number)[], (string | number)[]>;
    multiselectOptions: ComputedRef<IMultiselectOption[]>;
    isLoading: Ref<boolean>;
    selectedItems: ComputedRef<ISelectItem[]>;
    footer: ComputedRef<ModalStateFooter>;
    handleSelectionChange: (item: ISelectItem, state: string) => Promise<void>;
    handleMultiselectUpdate: (value: (string | number)[] | string | number | null) => Promise<void>;
    handleSearchChange: (query: string) => void;
};

export const useOrganisationFilter = (): OrganisationFilter=> {
	const { logError } = useRollbar();
	const searchStore = useSearchStore();
	const { close: closeMobileModal } = useMobileModal();
	const { filtersSelection, pageData } = storeToRefs(searchStore);

	const DEBOUNCE_TIME_MS = 300;
	const placeholder = 'Search by University';

	const selectedOrganisations = computed<IExtendedFilterValue[]>(() =>
		(filtersSelection.value.find((f) => f.type === FilterType.ORGANISATIONS)?.values as IExtendedFilterValue[]) ?? []
	);

	const selectedOrganisationIds = computed(() =>
		selectedOrganisations.value.map((v) => v.id)
	);

	const multiselectValue = ref<(string | number)[]>([...selectedOrganisationIds.value]);

	watch(selectedOrganisationIds, (ids) => {
		searchResults.value = [];
		const currentSet = new Set(multiselectValue.value);
		const newSet = new Set(ids);
		const isSame = currentSet.size === newSet.size && ids.every((id) => currentSet.has(id));

		if (isSame) return;

		multiselectValue.value = [...ids];
	});

	const facetCounts = computed(() => {
		const facets = (pageData.value?.filtersFacets ?? {}) as Record<
			string,
			IFacetKeyValuePair | undefined
		>;


		return facets[FilterType.ORGANISATIONS] || {} as IFacetKeyValuePair;
	});

	const searchResults = ref<IExtendedFilterValue[]>([...selectedOrganisations.value]);
	const isLoading = ref(false);
	let requestId = 0;

	watch(selectedOrganisations, (values) => {
		const searchResultIds = new Set(searchResults.value.map((org) => org.id));
		const mergedResults = [...searchResults.value];

		for (const organisation of values) {
			if (searchResultIds.has(organisation.id)) continue;

			mergedResults.push(organisation);
			searchResultIds.add(organisation.id);
		}

		searchResults.value = mergedResults;
	}, { deep: true });

	const knownOrganisations = computed(() => {
		const map = new Map<string, IExtendedFilterValue>();
		for (const organisation of searchResults.value) {
			map.set(organisation.id, organisation);
		}
		for (const organisation of selectedOrganisations.value) {
			map.set(organisation.id, organisation);
		}
		return map;
	});

	const selectedItems = computed<ISelectItem[]>(() =>
		selectedOrganisations.value.map((organisation) => ({
			value: organisation.id,
			text: organisation.name ?? organisation.id,
			count: facetCounts.value[organisation.id],
			state: SelectListItemStateType.SELECTED
		}))
	);

	const multiselectOptions = computed<IMultiselectOption[]>(() => {
		const optionsMap = new Map<string, IMultiselectOption>();

		for (const organisation of searchResults.value) {
			optionsMap.set(organisation.id, {
				value: organisation.id,
				label: organisation.name ?? organisation.id,
				count: facetCounts.value[organisation.id]
			});
		}

		return Array.from(optionsMap.values());
	});

	const fetchOrganisations = async (query: string): Promise<void> => {
		const trimmedQuery = query.trim();
		const currentRequestId = ++requestId;

		if (!trimmedQuery) {
			isLoading.value = false;
			searchResults.value = [...selectedOrganisations.value];
			return;
		}

		isLoading.value = true;

		const filters: ISearchQueryFilter[] = [{ type: FilterType.KEYWORD_WHAT, values: [trimmedQuery] }];

		try {
			const results = await environmentAwareFetch<ISearchResult<IOrganisationIdAndName>>(
				'/api/organisation-search',
				{
					method: 'POST',
					body: { filters }
				}
			);

			const mappedResults: IExtendedFilterValue[] = results.results.map((result) => ({
				id: result.organisationId.toString(),
				name: result.organisationName,
				virtualPath: result.virtualPath
			}));

			if (currentRequestId !== requestId) return;

			const seen = new Set<string>();
			const mergedResults = [...selectedOrganisations.value];

			for (const organisation of mergedResults) {
				seen.add(organisation.id);
			}

			for (const organisation of mappedResults) {
				if (seen.has(organisation.id)) continue;

				mergedResults.push(organisation);
				seen.add(organisation.id);

			}

			searchResults.value = mergedResults;
		} catch (error) {
			logError('Failed to fetch organisations', error as Error);
		} finally {
			if (currentRequestId === requestId) {
				isLoading.value = false;
			}
		}
	};

	const debouncedFetchOrganisations = debounce((query: string) => {
		void fetchOrganisations(query);
	}, DEBOUNCE_TIME_MS);

	const getUpdatedFilters = (
		organisationValue: IExtendedFilterValue,
		shouldSelect: boolean
	): { nextFilters: typeof filtersSelection.value; changed: boolean } => {
		const currentSelection = filtersSelection.value.find((filter) => filter.type === FilterType.ORGANISATIONS);
		const alreadySelected = currentSelection?.values.some((value) => value.id === organisationValue.id) ?? false;

		if (shouldSelect && alreadySelected) return { nextFilters: filtersSelection.value, changed: false };
		if (!shouldSelect && !alreadySelected) return { nextFilters: filtersSelection.value, changed: false };

		const remainingFilters = filtersSelection.value.filter((filter) => filter.type !== FilterType.ORGANISATIONS);

		if (shouldSelect) {
			const values = currentSelection ? [...currentSelection.values, organisationValue] : [organisationValue];
			return {
				nextFilters: [...remainingFilters, { type: FilterType.ORGANISATIONS, values }],
				changed: true
			};
		}

		if (!currentSelection) return { nextFilters: filtersSelection.value, changed: false };

		const remainingValues = currentSelection.values.filter((value) => value.id !== organisationValue.id);

		if (remainingValues.length === 0) {
			return { nextFilters: remainingFilters, changed: true };
		}

		return {
			nextFilters: [...remainingFilters, { ...currentSelection, values: remainingValues }],
			changed: true
		};
	};

	const getUpdatedFiltersForBatch = (
		addedIds: (string | number)[],
		removedIds: (string | number)[]
	): { nextFilters: typeof filtersSelection.value; changed: boolean } => {
		let currentFilters = filtersSelection.value;
		let hasChanges = false;

		// Process removals first
		for (const id of removedIds) {
			const organisationValue = knownOrganisations.value.get(String(id));
			if (!organisationValue) continue;

			const currentSelection = currentFilters.find((filter) => filter.type === FilterType.ORGANISATIONS);
			const isSelected = currentSelection?.values.some((value) => value.id === organisationValue.id) ?? false;

			if (!isSelected) continue;

			const remainingFilters = currentFilters.filter((filter) => filter.type !== FilterType.ORGANISATIONS);
			const remainingValues = currentSelection!.values.filter((value) => value.id !== organisationValue.id);

			if (remainingValues.length === 0) {
				currentFilters = remainingFilters;
			} else {
				currentFilters = [...remainingFilters, { ...currentSelection!, values: remainingValues }];
			}
			hasChanges = true;
		}

		// Process additions
		for (const id of addedIds) {
			const organisationValue = knownOrganisations.value.get(String(id));
			if (!organisationValue) continue;

			const currentSelection = currentFilters.find((filter) => filter.type === FilterType.ORGANISATIONS);
			const isSelected = currentSelection?.values.some((value) => value.id === organisationValue.id) ?? false;

			if (isSelected) continue;

			const remainingFilters = currentFilters.filter((filter) => filter.type !== FilterType.ORGANISATIONS);
			const values = currentSelection ? [...currentSelection.values, organisationValue] : [organisationValue];
			currentFilters = [...remainingFilters, { type: FilterType.ORGANISATIONS, values }];
			hasChanges = true;
		}

		return { nextFilters: currentFilters, changed: hasChanges };
	};

	const applyFiltersOnce = async (nextFilters: typeof filtersSelection.value): Promise<void> => {
		searchStore.filtersSelection = nextFilters;

		try {
			await searchStore.setRouteAndLoadData();
		} catch (error) {
			logError('Failed to apply filters', error as Error);
			throw new Error('Failed to apply filters:', { cause: error instanceof Error ? error : new Error(String(error)) });
		}
	};

	const updateSelection = async (organisationId: string, shouldSelect: boolean): Promise<void> => {
		const organisationValue = knownOrganisations.value.get(organisationId);
		if (!organisationValue) return;

		const { nextFilters, changed } = getUpdatedFilters(organisationValue, shouldSelect);
		if (!changed) return;

		await applyFiltersOnce(nextFilters);
	};

	const handleSelectionChange = async (item: ISelectItem, state: string): Promise<void> => {
		await updateSelection(
			String(item.value),
			state as SelectListItemStateType === SelectListItemStateType.SELECTED
		);
	};

	const handleMultiselectUpdate = async (value: (string | number)[] | string | number | null): Promise<void> => {
		const normalized = Array.isArray(value) ? value : value ? [value] : [];
		const prevSet = new Set(multiselectValue.value);
		const nextSet = new Set(normalized);

		const added = normalized.filter((v) => !prevSet.has(v));
		const removed = multiselectValue.value.filter((v) => !nextSet.has(v));

		const { nextFilters, changed } = getUpdatedFiltersForBatch(added, removed);

		if (changed) {
			await applyFiltersOnce(nextFilters);
		}
	};

	const handleSearchChange = (query: string): void => {
		isLoading.value = query.trim().length > 0;
		debouncedFetchOrganisations(query.trim());
	};

	const clearOrganisationFilters = async (): Promise<void> => {
		await searchStore.removeFilterByType([FilterType.ORGANISATIONS]);
		closeMobileModal();
	};

	const footer = computed<ModalStateFooter>(() => ({
		actionText: 'Add University',
		action: (): void => closeMobileModal(),
		actionType: 'secondary',
		clearText: 'Clear all',
		clear: (): void => void clearOrganisationFilters()
	}));

	return {
		placeholder,
		multiselectValue,
		multiselectOptions,
		isLoading,
		selectedItems,
		footer,
		handleSelectionChange,
		handleMultiselectUpdate,
		handleSearchChange
	};
};
